/**
 * Single source of truth for JTF Heavy's squadrons.
 *
 * Consumed by React components (named `import`) AND by the build-time
 * pre-render script (`scripts/prerender.js` via `require`), so this file is
 * authored as a plain CommonJS module with NO import/export syntax, and holds
 * only serialisable data (no JSX, no functions in the records).
 *
 * Each squadron drives:
 *   - a card on the home page and the /squadrons hub
 *   - its own page at /squadrons/<slug> (SquadronPage.js)
 *   - a <url> in the generated sitemap and a route in the pre-render list
 *
 * `slug` is unit + airframe on purpose (e.g. 472nd-f-16-viper) so the URL
 * carries the long-tail keyword we want to rank for ("dcs f-16 squadron").
 * `seoLabel` is the head phrase for the title/h1 eyebrow; the two Hornet units
 * get distinct labels (Marine vs Carrier) so they don't cannibalise.
 * `logo` uses an absolute path so it resolves on nested /squadrons/<slug> URLs.
 * `litho` points at a full-width airframe lithograph that may not exist yet;
 * SquadronPage renders a styled placeholder until the file is dropped in.
 */

const GROUPS = [
  {
    name: 'Air Combat Group (ACG)',
    blurb: 'Land-based fighter and attack squadrons providing air superiority, SEAD, and precision strike.',
    squadrons: [
      {
        slug: '472nd-f-16-viper',
        name: '472nd Fighter Squadron',
        shortName: '472nd FS',
        airframe: 'F-16C Viper',
        airframeShort: 'F-16C',
        seoLabel: 'DCS F-16 Squadron',
        logo: '/squadrons/472.webp',
        litho: '/pics/squadrons/litho/472nd-f-16-viper.webp',
        blurb: 'A multirole fighter squadron flying the F-16C. The 472nd handles suppression of enemy air defenses (SEAD), precision strike, and air-to-air tasking, the flexible tip of the ACG spear.',
        intro: `The 472nd Fighter Squadron flies the F-16C Viper for Joint Task Force Heavy. It is a multirole unit in the Air Combat Group, handling suppression of enemy air defenses (SEAD), precision strike, and air-to-air work.`,
        body: [
          { heading: 'Role in the fight', text: `The 472nd usually goes in first. We clear enemy radars and SAMs ahead of the strike package, then put our own bombs on target. Being multirole means the same jet can look after itself in the air if the fight comes to us.` },
          { heading: 'Flying the F-16C in DCS', text: `The Viper carries a bit of everything. For SEAD we run the HARM and the HTS pod. For strike we use GBU-12s, JDAM, JSOW, and Mavericks. For air-to-air we carry the AIM-120 and AIM-9. The datalink ties it together and helps the flight share one picture.` },
          { heading: `What you'll fly`, text: `Most sorties are hunter-killer SEAD, strike escort, or offensive counter-air. New pilots get trained from cold start through weapons employment. More experienced pilots work on SEAD timing and flying as a section or larger.` },
        ],
      },
      {
        slug: '484th-a-10-warthog',
        name: '484th Fighter Squadron',
        shortName: '484th FS',
        airframe: 'A-10C II Warthog',
        airframeShort: 'A-10C II',
        seoLabel: 'DCS A-10 Squadron',
        logo: '/squadrons/484.webp',
        litho: '/pics/squadrons/litho/484th-a-10-warthog.webp',
        blurb: 'The close air support specialists. Flying the A-10C II, the 484th delivers troops-in-contact CAS, forward air control, and battlefield interdiction with the gun and a full load of ordnance.',
        intro: `The 484th Fighter Squadron flies the A-10C II Warthog for Joint Task Force Heavy. It is the Air Combat Group's close air support unit.`,
        body: [
          { heading: 'Role in the fight', text: `When friendly ground forces need help, the 484th shows up. We run close air support off a 9-line, do forward air control for other flights, and hit targets behind the front line.` },
          { heading: 'Flying the A-10C II in DCS', text: `The Hog carries the GAU-8 gun, AGM-65 Mavericks, laser- and GPS-guided bombs, rockets, and a targeting pod. Most of the skill is in managing the sensors and putting weapons on the right target at low altitude.` },
          { heading: `What you'll fly`, text: `Expect close air support under real rules of engagement, hunting armor columns, and controlling other aircraft as a FAC-A. It is slow, deliberate flying with a lot of radio work alongside the ground war.` },
        ],
      },
      {
        slug: '584th-f-4-phantom',
        name: '584th Tactical Fighter Squadron',
        shortName: '584th TFS',
        airframe: 'F-4E Phantom II',
        airframeShort: 'F-4E Phantom II',
        seoLabel: 'DCS F-4 Phantom Squadron',
        logo: '/squadrons/584.webp',
        litho: '/pics/squadrons/litho/584th-f-4-phantom.webp',
        blurb: 'A hard-charging tactical fighter squadron operating the F-4E Phantom II, blending classic interceptor muscle with air-to-ground strike for a distinctly old-school combat role.',
        intro: `The 584th Tactical Fighter Squadron flies the F-4E Phantom II for Joint Task Force Heavy. It is an older, harder-edged jet that does both air-to-air and air-to-ground work.`,
        body: [
          { heading: 'Role in the fight', text: `The Phantom can intercept and it can strike. Two crew, two engines, and plenty of thrust let the 584th cover a wide range of missions in one airframe.` },
          { heading: 'Flying the F-4E in DCS', text: `The Phantom is a two-seat aircraft. The pilot flies and fights while the WSO runs the radar and works the weapons. We carry radar-guided AIM-7 Sparrows, AIM-9s, the gun, and a mix of unguided and guided air-to-ground ordnance.` },
          { heading: `What you'll fly`, text: `Crew coordination between pilot and WSO is the main thing you will learn here. Missions include intercepts and strikes, and the jet rewards a crew that works well together.` },
        ],
      },
    ],
  },
  {
    name: 'Marine Air Ground Task Force (MAGTF)',
    blurb: 'An expeditionary combined-arms group pairing Marine strike fighters with assault support and logistics.',
    squadrons: [
      {
        slug: 'vmfa-390-f-18-hornet',
        name: 'VMFA-390',
        shortName: 'VMFA-390',
        airframe: 'F/A-18C Hornet',
        airframeShort: 'F/A-18C',
        seoLabel: 'DCS Marine Hornet Squadron',
        logo: '/squadrons/390.webp',
        litho: '/pics/squadrons/litho/vmfa-390-f-18-hornet.webp',
        blurb: 'A Marine Fighter Attack squadron flying the F/A-18C in the expeditionary strike role, putting ordnance on target in support of the MAGTF ground scheme of maneuver.',
        intro: `VMFA-390 flies the F/A-18C Hornet for Joint Task Force Heavy as a Marine strike fighter squadron. It works in the expeditionary strike role for the Marine Air Ground Task Force.`,
        body: [
          { heading: 'Role in the fight', text: `VMFA-390 supports the ground plan. That means precision close air support, strike missions, and air-to-air cover over the landing area as part of the MAGTF.` },
          { heading: 'Flying the F/A-18C in DCS', text: `The Hornet is a good jet to learn on and still has plenty of depth. We use JDAM, JSOW, Maverick, and Harpoon for strike, and the AIM-120 and AIM-9 for the air-to-air fight. The cockpit is modern and easy to read.` },
          { heading: `What you'll fly`, text: `Strike packages, close air support for the ground element, and air-to-air as a flight. A good fit if you want one jet that can do most jobs.` },
        ],
      },
      {
        slug: '142nd-assault-helicopter',
        name: '142nd Assault Helicopter Company',
        shortName: '142nd AHC',
        airframe: 'Rotary Wing',
        airframeShort: 'Rotary',
        seoLabel: 'DCS Helicopter Squadron',
        logo: '/squadrons/142.webp',
        litho: '/pics/squadrons/litho/142nd-assault-helicopter.webp',
        blurb: 'Rotary-wing air assault and troop movement. The 142nd inserts and supports forces, flies armed escort, and provides the vertical lift the MAGTF depends on.',
        intro: `The 142nd Assault Helicopter Company is Joint Task Force Heavy's rotary-wing unit. It handles air assault, troop movement, and armed escort for the MAGTF.`,
        body: [
          { heading: 'Role in the fight', text: `The 142nd moves people and provides lift. We insert and pull out troops, fly escort for other helicopters, and support the ground fight from low altitude.` },
          { heading: 'Flying rotary wing in DCS', text: `Helicopters are a separate skill set from jets. There is hover work, landing in tight spaces, using terrain for cover, and staying in close contact with the ground element.` },
          { heading: 'Air cavalry heritage', text: `Alongside the assault mission, the 142nd flies in the air cavalry tradition: Kiowas scouting ahead of the ground force and Apaches hunting armor once the scouts find it. Reconnaissance and screening work sit next to troop lift as part of the same job.` },
          { heading: `What you'll fly`, text: `Air assault missions, casualty evacuation, and escort. New rotary pilots get brought up to speed, and experienced ones have plenty to work with.` },
        ],
      },
      {
        slug: 'vmgr-251-c-130-hercules',
        name: 'VMGR-251',
        shortName: 'VMGR-251',
        airframe: 'C-130J Hercules',
        airframeShort: 'C-130J Hercules',
        seoLabel: 'DCS C-130 Squadron',
        logo: '/squadrons/251.webp',
        litho: '/pics/squadrons/litho/vmgr-251-c-130-hercules.webp',
        blurb: 'Aerial refueling and tactical transport aboard the C-130J. VMGR-251 extends the reach of every other squadron with fuel, cargo, and airdrop support.',
        intro: `VMGR-251 flies the C-130 Hercules for Joint Task Force Heavy. It provides aerial refueling and tactical transport.`,
        body: [
          { heading: 'Role in the fight', text: `VMGR-251 keeps the rest of the force flying. We refuel fighters in the air, move cargo and people between bases, and run airdrops when a mission calls for it.` },
          { heading: 'Flying the Hercules in DCS', text: `Transport and tanker work is about steady airmanship: long navigation legs, holding a stable tanker track, and flying a clean approach. It is the support that makes bigger operations possible.` },
          { heading: `What you'll fly`, text: `Tanker orbits behind strike packages, logistics runs between bases, and airdrops. A good place for pilots who like the support role and the larger picture.` },
        ],
      },
    ],
  },
  {
    name: 'Carrier Air Wing 62 (CVW-62)',
    blurb: 'Blue-water naval aviation flying from the carrier deck for fleet defense and maritime strike.',
    squadrons: [
      {
        slug: 'vfa-119-f-18-hornet',
        name: 'VFA-119',
        shortName: 'VFA-119',
        airframe: 'F/A-18C Hornet',
        airframeShort: 'F/A-18C',
        seoLabel: 'DCS Carrier Hornet Squadron',
        logo: '/squadrons/119.webp',
        litho: '/pics/squadrons/litho/vfa-119-f-18-hornet.webp',
        blurb: 'A carrier-based strike fighter squadron flying the F/A-18C off the boat, the backbone of CVW-62 for maritime strike and multirole tasking at sea.',
        intro: `VFA-119 flies the F/A-18C Hornet off the carrier for Joint Task Force Heavy. It is the main strike fighter squadron of Carrier Air Wing 62.`,
        body: [
          { heading: 'Role in the fight', text: `VFA-119 works from the boat. We launch for maritime strike, general strike, and fleet defense, and recover back aboard day or night.` },
          { heading: 'Flying the F/A-18C off the carrier in DCS', text: `Carrier operations are the hard part. There are Case I overhead patterns, Case III instrument approaches, and the constant goal of a good trap. On the strike side we carry JDAM, Harpoon, and the rest of the Hornet loadout.` },
          { heading: `What you'll fly`, text: `Carrier qualification, maritime strike, and combat air patrol over the group. This is the squadron for pilots who want to land on a moving ship.` },
        ],
      },
      {
        slug: 'vf-172-f-14-tomcat',
        name: 'VF-172',
        shortName: 'VF-172',
        airframe: 'F-14B Tomcat',
        airframeShort: 'F-14B',
        seoLabel: 'DCS F-14 Tomcat Squadron',
        logo: '/squadrons/172.webp',
        litho: '/pics/squadrons/litho/vf-172-f-14-tomcat.webp',
        blurb: 'Fleet defense in the F-14B Tomcat. VF-172 owns the long-range intercept and combat air patrol mission, guarding the carrier group with the Phoenix and Sparrow.',
        intro: `VF-172 flies the F-14B Tomcat for Joint Task Force Heavy. It handles fleet defense and long-range intercepts for Carrier Air Wing 62.`,
        body: [
          { heading: 'Role in the fight', text: `VF-172 guards the carrier group from a distance. We fly combat air patrol and long-range intercepts to deal with threats before they get close.` },
          { heading: 'Flying the F-14B in DCS', text: `The Tomcat needs two crew, a pilot and a RIO. The RIO runs the AWG-9 radar and the AIM-54 Phoenix while the pilot flies the jet. It takes practice to get the crew working smoothly.` },
          { heading: `What you'll fly`, text: `Pilot and RIO crews flying patrol, intercepts, and escort. Bring some patience for the systems and we will get you up to speed.` },
        ],
      },
    ],
  },
  {
    name: 'Auxiliary Corps',
    blurb: 'Specialist units that round out the force: adversary training, demonstration flying, and logistics.',
    squadrons: [
      {
        slug: 'blue-tide-demo-team',
        name: 'Blue Tide Demonstration Team',
        shortName: 'Blue Tide',
        airframe: 'F/A-18C Hornet',
        airframeShort: 'F/A-18C',
        seoLabel: 'DCS Flight Demonstration Team',
        logo: '/squadrons/blue.webp',
        litho: '/pics/squadrons/litho/blue-tide-demo-team.webp',
        blurb: `HVY's precision flight demonstration team. Flying the F/A-18C, Blue Tide performs formation aerobatics and represents the community at events and airshows.`,
        intro: `Blue Tide is Joint Task Force Heavy's flight demonstration team, flying the F/A-18C Hornet in formation aerobatics at events and airshows.`,
        body: [
          { heading: 'Role in the community', text: `Blue Tide represents the community with tight formation flying and rehearsed routines. The point is to fly it cleanly and consistently every time.` },
          { heading: 'Flying the demo in DCS', text: `Demonstration flying is about precision and trust. Holding a few feet of spacing through loops and crosses takes a lot of practice and constant awareness of the other aircraft.` },
          { heading: `What you'll fly`, text: `Formation aerobatics, practice sorties, and public shows. A good fit for pilots with a steady hand who want to get good at formation flying.` },
        ],
      },
      {
        slug: '23rd-aggressors-squadron',
        name: '23rd Aggressors Squadron',
        shortName: '23rd AGRS',
        airframe: 'Adversary Air',
        airframeShort: 'Aggressors',
        seoLabel: 'DCS Aggressor Squadron',
        logo: '/squadrons/23.webp',
        litho: '/pics/squadrons/litho/23rd-aggressors-squadron.webp',
        blurb: 'The red-air professionals. The 23rd flies dedicated adversary tactics to train the rest of the force, replicating threat aircraft and doctrine in realistic engagements.',
        intro: `The 23rd Aggressors Squadron is Joint Task Force Heavy's adversary air unit. It flies red air to train the rest of the community.`,
        body: [
          { heading: 'Role in the fight', text: `The 23rd plays the opposing force so the other squadrons get better. We copy enemy aircraft, tactics, and doctrine to give a realistic threat to train against.` },
          { heading: 'Flying red air in DCS', text: `Aggressor flying means knowing both your own jet and the threat you are imitating, and flying to that threat's strengths and limits rather than your own. It is a good way to sharpen your air-to-air skills.` },
          { heading: `What you'll fly`, text: `Adversary sorties for training events, basic and advanced maneuvering instruction, and dissimilar air combat against the line squadrons. A good fit if you like the air-to-air game.` },
        ],
      },
      {
        slug: 'phantom-logistics',
        name: 'Phantom Logistics',
        shortName: 'Phantom Logistics',
        airframe: 'Civil Aviation',
        airframeShort: 'Civ',
        seoLabel: 'DCS Logistics Squadron',
        logo: '/squadrons/pl.webp',
        litho: '/pics/squadrons/litho/phantom-logistics.webp',
        blurb: 'Civilian and logistics operations. Phantom Logistics moves people and cargo behind the lines, adding a living rear-area dimension to HVY campaigns.',
        intro: `Phantom Logistics is Joint Task Force Heavy's civilian and logistics unit. It adds the rear-area side of a campaign that keeps everything else supplied.`,
        body: [
          { heading: 'Role in the campaign', text: `Phantom Logistics moves people and cargo behind the lines. It adds a working supply side to HVY campaigns instead of leaving it as an abstraction.` },
          { heading: 'Flying logistics in DCS', text: `From airliners to cargo aircraft, logistics flying is about procedure, navigation, and getting the aircraft where it needs to be.` },
          { heading: `What you'll fly`, text: `Scheduled cargo and passenger runs, campaign resupply, and civil operations. A niche for pilots who enjoy the process and the roleplay as much as the payload.` },
        ],
      },
    ],
  },
];

// Flat list (source of truth for routes/sitemap), each carrying its group name.
const SQUADRONS = GROUPS.reduce(
  (acc, g) => acc.concat(g.squadrons.map((s) => Object.assign({ group: g.name }, s))),
  []
);

const getSquadron = (slug) => SQUADRONS.find((s) => s.slug === slug);

module.exports = { GROUPS, SQUADRONS, getSquadron };
